import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  ClockIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

interface FormData {
  name: string;
  email: string;
  company: string;
  projectType: string;
  budget: string;
  timeline: string;
  description: string;
  goals: string[];
  contactMethod: string;
}

const Contact: React.FC = () => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    projectType: '',
    budget: '',
    timeline: '',
    description: '',
    goals: [],
    contactMethod: 'email'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const totalSteps = 4;

  const projectTypes = [
    { id: 'ai-implementation', name: t('contact.projectTypes.aiImplementation') },
    { id: 'business-automation', name: t('contact.projectTypes.businessAutomation') },
    { id: 'data-pipelines', name: t('contact.projectTypes.dataPipelines') },
    { id: 'cross-cultural-consulting', name: t('contact.projectTypes.crossCulturalConsulting') },
    { id: 'other', name: t('contact.projectTypes.other') }
  ];

  const budgetRanges = [
    { id: 'under-10k', name: t('contact.budget.under10k') },
    { id: '10k-25k', name: t('contact.budget.10k25k') },
    { id: '25k-50k', name: t('contact.budget.25k50k') },
    { id: '50k-100k', name: t('contact.budget.50k100k') },
    { id: 'over-100k', name: t('contact.budget.over100k') }
  ];

  const timelines = [
    { id: 'asap', name: t('contact.timeline.asap') },
    { id: '1-3-months', name: t('contact.timeline.1to3months') },
    { id: '3-6-months', name: t('contact.timeline.3to6months') },
    { id: '6-12-months', name: t('contact.timeline.6to12months') },
    { id: 'flexible', name: t('contact.timeline.flexible') }
  ];

  const goals = [
    { id: 'increase-efficiency', name: t('contact.goals.increaseEfficiency') },
    { id: 'reduce-costs', name: t('contact.goals.reduceCosts') },
    { id: 'improve-accuracy', name: t('contact.goals.improveAccuracy') },
    { id: 'scale-operations', name: t('contact.goals.scaleOperations') },
    { id: 'digital-transformation', name: t('contact.goals.digitalTransformation') },
    { id: 'competitive-advantage', name: t('contact.goals.competitiveAdvantage') }
  ];

  const handleInputChange = (field: keyof FormData, value: string | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGoalToggle = (goalId: string) => {
    const updatedGoals = formData.goals.includes(goalId)
      ? formData.goals.filter(g => g !== goalId)
      : [...formData.goals, goalId];
    handleInputChange('goals', updatedGoals);
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          language: 'en' // or get from i18n
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setIsSubmitted(true);
      } else {
        console.error('Form submission failed:', result);
        // TODO: Show error message to user
        alert('Submission failed. Please try again.');
      }
    } catch (error) {
      console.error('Network error:', error);
      alert('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!(formData.name && formData.email && formData.company);
      case 2:
        return !!(formData.projectType && formData.budget && formData.timeline);
      case 3:
        return !!(formData.description && formData.goals.length > 0);
      case 4:
        return true;
      default:
        return false;
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="space-y-6"
          >
            <h3 className="text-2xl font-bold text-white mb-6">
              {t('contact.step1.title')}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-300 mb-2">
                  {t('contact.step1.name')} *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-primary-500 focus:outline-none"
                  placeholder={t('contact.step1.namePlaceholder')}
                />
              </div>
              
              <div>
                <label className="block text-gray-300 mb-2">
                  {t('contact.step1.email')} *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-primary-500 focus:outline-none"
                  placeholder={t('contact.step1.emailPlaceholder')}
                />
              </div>
            </div>
            
            <div>
              <label className="block text-gray-300 mb-2">
                {t('contact.step1.company')} *
              </label>
              <input
                type="text"
                value={formData.company}
                onChange={(e) => handleInputChange('company', e.target.value)}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-primary-500 focus:outline-none"
                placeholder={t('contact.step1.companyPlaceholder')}
              />
            </div>
          </motion.div>
        );
        
      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="space-y-6"
          >
            <h3 className="text-2xl font-bold text-white mb-6">
              {t('contact.step2.title')}
            </h3>
            
            <div>
              <label className="block text-gray-300 mb-4">
                {t('contact.step2.projectType')} *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {projectTypes.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => handleInputChange('projectType', type.id)}
                    className={`p-4 rounded-lg border transition-all duration-300 text-left ${
                      formData.projectType === type.id
                        ? 'border-primary-500 bg-primary-500/10 text-primary-300'
                        : 'border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500'
                    }`}
                  >
                    {type.name}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-gray-300 mb-4">
                {t('contact.step2.budget')} *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {budgetRanges.map((budget) => (
                  <button
                    key={budget.id}
                    type="button"
                    onClick={() => handleInputChange('budget', budget.id)}
                    className={`p-3 rounded-lg border transition-all duration-300 text-center ${
                      formData.budget === budget.id
                        ? 'border-primary-500 bg-primary-500/10 text-primary-300'
                        : 'border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500'
                    }`}
                  >
                    {budget.name}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <label className="block text-gray-300 mb-4">
                {t('contact.step2.timeline')} *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {timelines.map((timeline) => (
                  <button
                    key={timeline.id}
                    type="button"
                    onClick={() => handleInputChange('timeline', timeline.id)}
                    className={`p-3 rounded-lg border transition-all duration-300 text-center ${
                      formData.timeline === timeline.id
                        ? 'border-primary-500 bg-primary-500/10 text-primary-300'
                        : 'border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500'
                    }`}
                  >
                    {timeline.name}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        );
        
      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="space-y-6"
          >
            <h3 className="text-2xl font-bold text-white mb-6">
              {t('contact.step3.title')}
            </h3>
            
            <div>
              <label className="block text-gray-300 mb-2">
                {t('contact.step3.description')} *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={5}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:border-primary-500 focus:outline-none resize-none"
                placeholder={t('contact.step3.descriptionPlaceholder')}
              />
            </div>
            
            <div>
              <label className="block text-gray-300 mb-4">
                {t('contact.step3.goals')} *
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {goals.map((goal) => (
                  <button
                    key={goal.id}
                    type="button"
                    onClick={() => handleGoalToggle(goal.id)}
                    className={`p-4 rounded-lg border transition-all duration-300 text-left flex items-center space-x-3 ${
                      formData.goals.includes(goal.id)
                        ? 'border-primary-500 bg-primary-500/10 text-primary-300'
                        : 'border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500'
                    }`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                      formData.goals.includes(goal.id)
                        ? 'border-primary-500 bg-primary-500'
                        : 'border-gray-500'
                    }`}>
                      {formData.goals.includes(goal.id) && (
                        <CheckCircleIcon className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <span>{goal.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        );
        
      case 4:
        return (
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            className="space-y-6"
          >
            <h3 className="text-2xl font-bold text-white mb-6">
              {t('contact.step4.title')}
            </h3>
            
            <div className="bg-gray-700/50 rounded-lg p-6 space-y-4">
              <h4 className="text-lg font-semibold text-white">
                {t('contact.step4.summary')}
              </h4>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-400">{t('contact.step1.name')}:</span>
                  <span className="text-white ml-2">{formData.name}</span>
                </div>
                <div>
                  <span className="text-gray-400">{t('contact.step1.company')}:</span>
                  <span className="text-white ml-2">{formData.company}</span>
                </div>
                <div>
                  <span className="text-gray-400">{t('contact.step2.projectType')}:</span>
                  <span className="text-white ml-2">
                    {projectTypes.find(p => p.id === formData.projectType)?.name}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400">{t('contact.step2.budget')}:</span>
                  <span className="text-white ml-2">
                    {budgetRanges.find(b => b.id === formData.budget)?.name}
                  </span>
                </div>
              </div>
            </div>
            
            <div>
              <label className="block text-gray-300 mb-4">
                {t('contact.step4.contactMethod')}
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => handleInputChange('contactMethod', 'email')}
                  className={`p-4 rounded-lg border transition-all duration-300 flex items-center space-x-3 ${
                    formData.contactMethod === 'email'
                      ? 'border-primary-500 bg-primary-500/10 text-primary-300'
                      : 'border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500'
                  }`}
                >
                  <EnvelopeIcon className="w-5 h-5" />
                  <span>{t('contact.step4.email')}</span>
                </button>
                
                <button
                  type="button"
                  onClick={() => handleInputChange('contactMethod', 'phone')}
                  className={`p-4 rounded-lg border transition-all duration-300 flex items-center space-x-3 ${
                    formData.contactMethod === 'phone'
                      ? 'border-primary-500 bg-primary-500/10 text-primary-300'
                      : 'border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500'
                  }`}
                >
                  <PhoneIcon className="w-5 h-5" />
                  <span>{t('contact.step4.phone')}</span>
                </button>
              </div>
            </div>
          </motion.div>
        );
        
      default:
        return null;
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center space-y-6"
        >
          <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto">
            <CheckCircleIcon className="w-12 h-12 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-white">
            {t('contact.success.title')}
          </h2>
          <p className="text-gray-300 max-w-md">
            {t('contact.success.message')}
          </p>
          <button
            onClick={() => {
              setIsSubmitted(false);
              setCurrentStep(1);
              setFormData({
                name: '',
                email: '',
                company: '',
                projectType: '',
                budget: '',
                timeline: '',
                description: '',
                goals: [],
                contactMethod: 'email'
              });
            }}
            className="btn-primary"
          >
            {t('contact.success.newConsultation')}
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="space-y-12">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center"
      >
        <h1 className="text-4xl lg:text-5xl font-bold text-white mb-4">
          {t('contact.title')}
        </h1>
        <p className="text-lg text-gray-300 max-w-3xl mx-auto">
          {t('contact.subtitle')}
        </p>
      </motion.header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
        {/* Contact Info */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, x: -30 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="space-y-8"
        >
          <div className="bg-gray-800 border border-gray-700 rounded-2xl p-6">
            <h3 className="text-xl font-bold text-white mb-6">
              {t('contact.info.title')}
            </h3>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <EnvelopeIcon className="w-5 h-5 text-primary-400" />
                <span className="text-gray-300">jean@jeanbilbao.com</span>
              </div>
              
              <div className="flex items-center space-x-3">
                <PhoneIcon className="w-5 h-5 text-primary-400" />
                <span className="text-gray-300">+1 (555) 123-4567</span>
              </div>
              
              <div className="flex items-center space-x-3">
                <MapPinIcon className="w-5 h-5 text-primary-400" />
                <span className="text-gray-300">{t('contact.info.location')}</span>
              </div>
              
              <div className="flex items-center space-x-3">
                <ClockIcon className="w-5 h-5 text-primary-400" />
                <span className="text-gray-300">{t('contact.info.hours')}</span>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-primary-500/10 to-secondary-500/10 border border-primary-500/20 rounded-2xl p-6">
            <h4 className="text-lg font-semibold text-white mb-3">
              {t('contact.info.responseTime')}
            </h4>
            <p className="text-gray-300 text-sm">
              {t('contact.info.responseDescription')}
            </p>
          </div>
        </motion.div>

        {/* Consultation Funnel */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="lg:col-span-2"
        >
          <div className="bg-gray-800 border border-gray-700 rounded-2xl p-8">
            {/* Progress Bar */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-300">
                  {t('contact.step')} {currentStep} {t('contact.of')} {totalSteps}
                </span>
                <span className="text-primary-400 font-medium">
                  {Math.round((currentStep / totalSteps) * 100)}%
                </span>
              </div>
              
              <div className="w-full bg-gray-700 rounded-full h-2">
                <motion.div
                  className="bg-gradient-to-r from-primary-500 to-secondary-500 h-2 rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: `${(currentStep / totalSteps) * 100}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <AnimatePresence mode="wait">
                {renderStep()}
              </AnimatePresence>

              {/* Navigation */}
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-700">
                <button
                  type="button"
                  onClick={prevStep}
                  disabled={currentStep === 1}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-all duration-300 ${
                    currentStep === 1
                      ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  <ArrowLeftIcon className="w-5 h-5" />
                  <span>{t('contact.previous')}</span>
                </button>

                {currentStep < totalSteps ? (
                  <button
                    type="button"
                    onClick={nextStep}
                    disabled={!isStepValid(currentStep)}
                    className={`flex items-center space-x-2 px-6 py-3 rounded-lg transition-all duration-300 ${
                      isStepValid(currentStep)
                        ? 'bg-primary-500 text-white hover:bg-primary-600'
                        : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <span>{t('contact.next')}</span>
                    <ArrowRightIcon className="w-5 h-5" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex items-center space-x-2 px-8 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>{t('contact.submitting')}</span>
                      </>
                    ) : (
                      <>
                        <span>{t('contact.submit')}</span>
                        <ArrowRightIcon className="w-5 h-5" />
                      </>
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;